/**
 * DlgOverrideModal - Capture reason when assigning a SATURATED technician
 * 
 * Phase 3: Diagnostic Load Guard (DLG) Frontend
 * Triggered when user clicks "Assign" on a tech with dlgState === 'saturated'.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { ShieldAlert, Loader2 } from 'lucide-react';

export type DlgOverrideReason =
  | 'best_fit'
  | 'others_unavailable'
  | 'tech_preference'
  | 'training_mentorship'
  | 'shop_needs';

export const DLG_OVERRIDE_OPTIONS: Array<{ value: DlgOverrideReason; label: string; description: string }> = [
  { value: 'best_fit', label: 'Best fit for this vehicle', description: 'This tech has the right skills/history for this job' },
  { value: 'others_unavailable', label: 'Others unavailable', description: 'No other qualified techs can take this' },
  { value: 'tech_preference', label: 'Tech preference', description: 'Technician wants to take this on' },
  { value: 'training_mentorship', label: 'Training / mentorship', description: 'Learning opportunity or supervised work' },
  { value: 'shop_needs', label: 'Shop needs right now', description: 'Operational necessity overrides advisory' },
];

interface DlgOverrideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: DlgOverrideReason, note: string) => void;
  techName: string;
  dlgExplanation?: string;
  isLoading?: boolean;
}

export function DlgOverrideModal({
  open,
  onOpenChange,
  onConfirm,
  techName,
  dlgExplanation,
  isLoading = false,
}: DlgOverrideModalProps) {
  const [reason, setReason] = useState<DlgOverrideReason | ''>('');
  const [note, setNote] = useState('');

  const handleConfirm = () => {
    if (!reason) return;
    onConfirm(reason, note.trim());
    setReason('');
    setNote('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setReason('');
      setNote('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-orange-500" />
            Diagnostic Load Advisory
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium">{techName}</span> may benefit from a break from diagnostics.
            {dlgExplanation && (
              <span className="block mt-1 text-xs">{dlgExplanation}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <Label>Why are you proceeding with this assignment?</Label>
            <RadioGroup value={reason} onValueChange={(v) => setReason(v as DlgOverrideReason)}>
              {DLG_OVERRIDE_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-start space-x-3">
                  <RadioGroupItem value={option.value} id={`dlg-${option.value}`} className="mt-1" />
                  <Label htmlFor={`dlg-${option.value}`} className="flex flex-col cursor-pointer">
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs text-muted-foreground">{option.description}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dlg-note">Additional Note (optional)</Label>
            <Textarea
              id="dlg-note"
              placeholder="Any additional context..."
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 200))}
              className="h-16"
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground text-right">{note.length}/200</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!reason || isLoading}
          >
            {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Confirm Assignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
